// Environment variables for Shopify domain and access token
const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const STOREFRONT_ACCESS_TOKEN = process.env.REACT_APP_STOREFRONT_ACCESS_TOKEN;
const SHOPIFY_API_VERSION = '2025-04'; // Define API version for reuse

// =====================================================================
// Helper: Generic Shopify API Fetch Function
// =====================================================================
const shopifyFetch = async (query, variables = {}) => {
  const endpoint = `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
  };
  const body = JSON.stringify({ query, variables });

  console.log(`Fetching from Shopify: ${query.split('\n')[1]?.trim()?.split(' ')[0]}...`); // Log operation type
  // console.log("Endpoint:", endpoint);
  // console.log("Body:", body); // Optional: Log full body for debugging

  try {
    const fetchResponse = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    const responseData = await fetchResponse.json();
    // console.log("Shopify fetch response data:", responseData); // Optional: Log full response

    if (!fetchResponse.ok || responseData.errors) {
      console.error('Error response from Shopify API:', responseData.errors || responseData);
      const errorMessages = responseData.errors?.map(err => err.message).join("; ") || `HTTP error! status: ${fetchResponse.status}`;
      throw new Error(`Shopify API request failed: ${errorMessages}`);
    }
    
    // Specific handling for userErrors within data structure (e.g., cart mutations)
    const operationName = query.match(/mutation\s+(\w+)/)?.[1] || query.match(/query\s+(\w+)/)?.[1];
    if (operationName && responseData.data?.[operationName]?.userErrors?.length > 0) {
      console.error(`User errors during Shopify operation ${operationName}:`, responseData.data[operationName].userErrors);
      const userErrorMessages = responseData.data[operationName].userErrors.map(err => err.message).join("; ");
      throw new Error(`Shopify operation ${operationName} failed: ${userErrorMessages}`);
    }

    return responseData.data; // Return only the data part

  } catch (error) {
    console.error(`Error during shopifyFetch (${query.split('\n')[1]?.trim()?.split(' ')[0]}):`, error);
    throw error; // Re-throw error for upstream handling
  }
};

// =====================================================================
// Products Query
// =====================================================================
const productQuery = `
  query {
    products(first: 10) {
      edges {
        cursor
        node {
          id
          title
          description
          images(first: 10) {
            edges {
              node {
                src
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              cursor
              node {
                id
                title
                price {
                  amount
                }
                compareAtPrice {
                  amount
                }
                availableForSale
              }
            }
          }
        }
      }
    }
  }
`;

export const getProducts = async () => {
  try {
    const data = await shopifyFetch(productQuery);
    if (!data || !data.products) {
        throw new Error("Invalid response structure from getProducts");
    }
    return data.products.edges.map(edge => edge.node);
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// =====================================================================
// Cart API Methods
// =====================================================================

// Mutation: Create a cart with initial line items
const cartCreateMutation = `
  mutation cartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        createdAt
        updatedAt
        checkoutUrl
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                  }
                  compareAtPrice {
                    amount
                  }
                }
              }
            }
          }
        }
        attributes {
          key
          value
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// Function to create a new cart with line items
export const createCart = async (lineItems) => {
  console.log('Creating cart with line items:', lineItems);
  try {
    const inputObject = { input: { lines: lineItems } };
    const data = await shopifyFetch(cartCreateMutation, inputObject);
    
    if (!data || !data.cartCreate || !data.cartCreate.cart) {
      console.error("Invalid response structure from createCart");
      throw new Error("Failed to create cart: Invalid response.");
    }

    console.log("Cart creation response:", data.cartCreate);
    return data.cartCreate.cart;
  } catch (error) {
    console.error("Error creating cart:", error);
    throw error;
  }
};

// Mutation: Add line items to an existing cart
const cartLinesAddMutation = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 10){
            edges
            {
                node{
                    quantity
                    merchandise{
                        ... on ProductVariant {   						
                            id
                        }
                    }
                }
            }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
          totalDutyAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const addCartLines = async (cartId, lineItems) => {
  console.log('Adding cart lines:');
  console.log('Cart ID:', cartId);
  console.log('Line items:', lineItems);

  if (!cartId) {
    const errorMsg = "addCartLines called with invalid cartId.";
    console.error(errorMsg, cartId);
    throw new Error(errorMsg);
  }
  if (!lineItems || !Array.isArray(lineItems) || lineItems.length === 0) {
    const errorMsg = "addCartLines called with invalid or empty lineItems.";
    console.error(errorMsg, lineItems);
    throw new Error(errorMsg);
  }

  try {
    const variables = { cartId, lines: lineItems };
    const data = await shopifyFetch(cartLinesAddMutation, variables);

    if (!data || !data.cartLinesAdd || !data.cartLinesAdd.cart) {
      console.error("Invalid response structure from addCartLines");
      throw new Error("Failed to add cart lines: Invalid response.");
    }

    console.log("Add cart lines response:", data.cartLinesAdd);
    return data.cartLinesAdd.cart;
  } catch (error) {
    // Error already logged in shopifyFetch, just re-throw
    throw error;
  }
};

// =====================================================================
// Helper Function: Create Cart and Add Items (Two-Step)
// =====================================================================
/**
 * Creates a new cart and immediately adds line items to it.
 * This function first creates an empty cart and then uses the addCartLines 
 * function to add the specified items. This can help diagnose issues where 
 * adding items during initial cart creation fails silently.
 * 
 * @param {Array} lineItems - An array of line item objects, e.g., [{ merchandiseId: "gid://...", quantity: 1 }]
 * @returns {Promise<Object>} - The final cart object after adding items.
 */
export const createCartAndAddItems = async (lineItems) => {
  console.log("Starting two-step cart creation with items:", lineItems);
  
  try {
    // Step 1: Create an empty cart
    console.log("Step 1: Creating an empty cart...");
    const emptyCart = await createCart([]); // Pass empty array for lines
    
    // === Enhanced Logging: Log the entire emptyCart object ===
    console.log("Step 1 Response (emptyCart object):", JSON.stringify(emptyCart, null, 2));
    // === End Enhanced Logging ===

    if (!emptyCart || !emptyCart.id) {
      console.error("Failed to create an empty cart in step 1.");
      throw new Error("Could not create an empty cart.");
    }
    const cartId = emptyCart.id;
    
    // === Enhanced Logging: Log cartId and its type ===
    console.log(`Step 1 extracted cartId: ${cartId} (Type: ${typeof cartId})`);
    // === End Enhanced Logging ===

    // Step 2: Add the desired line items to the newly created cart
    console.log(`Step 2: Adding items to cart ${cartId}...`);
    const finalCart = await addCartLinesDirectFetch(cartId, lineItems);

    if (!finalCart) {
      console.error(`Failed to add items to cart ${cartId} in step 2.`);
      // Note: Depending on Shopify API, addCartLines might return the cart 
      // even if some lines failed. You might need more robust error checking here
      // based on the exact response structure or userErrors if they appear.
      throw new Error("Could not add items to the cart.");
    }

    console.log("Step 2 successful. Final cart state:", finalCart);
    return finalCart; // Return the cart state after adding items

  } catch (error) {
    console.error("Error during two-step cart creation and addition:", error);
    // Re-throw the error so the calling code can handle it
    throw error; 
  }
};

// =====================================================================
// Direct Fetch Implementation for Adding Cart Lines
// =====================================================================
const addCartLinesDirectFetch = async (cartId, lineItems) => {
  const endpoint = `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
  };
  const body = JSON.stringify({
    query: cartLinesAddMutation, // Reuse the existing mutation string
    variables: {
      cartId: cartId, 
      lines: lineItems, 
    },
  });

  console.log("Attempting addCartLines with direct fetch...");
  console.log("Endpoint:", endpoint);
  console.log("Body:", body); // Log the exact body being sent

  try {
    const fetchResponse = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    const responseData = await fetchResponse.json();
    console.log("Direct fetch response data:", responseData);

    if (!fetchResponse.ok || responseData.errors) {
      console.error('Error response from direct fetch:', responseData.errors || responseData);
      const errorMessages = responseData.errors?.map(err => err.message).join("; ") || `HTTP error! status: ${fetchResponse.status}`;
      throw new Error(`Direct fetch failed: ${errorMessages}`);
    }
    
    if (responseData.data?.cartLinesAdd?.userErrors?.length > 0) {
      console.error("User errors during direct fetch add cart lines:", responseData.data.cartLinesAdd.userErrors);
      throw new Error("Failed to add cart lines (direct fetch)");
    }

    return responseData.data?.cartLinesAdd?.cart;

  } catch (error) {
    console.error("Error during addCartLinesDirectFetch:", error);
    throw error;
  }
};
